// v3.83 - NLP-02: Boundary detection handling + askAnyway override path - 2026-03-15
// v3.60 - NLP Chat Hook - 2026-03-03
//
// Manages chat state for ChatWidget:
//   - messages[] displayed in the UI
//   - session_id persisted in sessionStorage (clears on tab close)
//   - clarifier state (question + options from pipeline-runner)
//   - sendMessage(), selectClarifier(), reset()
//
// sessionStorage key: nlp_session_id
// Rate-limit (429): surfaced as an error message in the chat

import { useState, useRef, useCallback } from 'react';
import {
  nlpChatService,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  NlpClarifier,
  NlpChatResponse,
} from '../services/api';
// v1.0 - CHAT-ERR-SAFE-LIST-01: only surface backend reasons on the shared safe list - 2026-09-13
import { resolveChatError } from '../utils/chatErrorSafeList';

export type MessageRole = 'user' | 'assistant' | 'error' | 'boundary';

export interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  citations?: NlpChatResponse['citations'];
  latency_ms?: number;
  cache_hit?: boolean;
  // v3.77 - SHOW-01: trace entity ID for "How we found this" panel
  message_id?: string;
  // v3.83 - NLP-02: boundary detection
  boundary_level?: 'WARN' | 'REJECT';
  original_text?: string;   // user's original question — for "Ask Anyway"
  // v3.83 - NLP-02: general knowledge override
  is_general_knowledge?: boolean;
  disclaimer?: string;
  // v3.94 - MISSED-ID-04 EDGS: employment decision risk
  risk_level?: string;
  disclaimer_text?: string;
  // v4.04 - STORY-171: answer consistency change notification
  answer_changed?: boolean;
  divergence_pct?: number;
  // v4.07 - AUDIT-12: inline confidence badge
  confidence_label?: string;
}

interface ClarifierState {
  question: string;
  options: string[];
  allows_multiple: boolean;
  contextId: string;
  selected: string[];
}

export interface UseChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  clarifiers: ClarifierState | null;
  sendMessage: (text: string) => Promise<void>;
  // v3.83 - NLP-02: re-send original question with override=true (bypasses boundary)
  askAnyway: (originalText: string) => Promise<void>;
  selectClarifier: (option: string) => void;
  submitClarifiers: () => Promise<void>;
  reset: () => void;
}

const SESSION_STORAGE_KEY = 'nlp_session_id';

function makeId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function useChat(): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [clarifiers, setClarifiers] = useState<ClarifierState | null>(null);
  const sessionIdRef = useRef<string | undefined>(
    sessionStorage.getItem(SESSION_STORAGE_KEY) || undefined
  );

  // v3.60 - Append a message to the chat history
  const appendMessage = useCallback((msg: ChatMessage) => {
    setMessages(prev => [...prev, msg]);
  }, []);

  // v3.60 - Core send: posts to /api/nlp/chat and processes the response
  // v3.83 - NLP-02: added override param for boundary bypass path
  const doSend = useCallback(
    async (
      text: string,
      clarifierContextId?: string,
      selectedClarifiers?: string[],
      override?: boolean,
    ) => {
      setIsLoading(true);

      try {
        const resp = await nlpChatService.sendMessage({
          message: text,
          session_id: sessionIdRef.current,
          clarifier_context_id: clarifierContextId,
          selected_clarifiers: selectedClarifiers,
          ...(override ? { override: true } : {}),
        });

        // v3.60 - Persist session ID across navigations in same tab
        if (resp.session_id) {
          sessionIdRef.current = resp.session_id;
          sessionStorage.setItem(SESSION_STORAGE_KEY, resp.session_id);
        }

        // v3.83 - NLP-02: boundary gate — show inline warning instead of pipeline result
        if (resp.boundary_level === 'WARN' || resp.boundary_level === 'REJECT') {
          setClarifiers(null);
          appendMessage({
            id: makeId(),
            role: 'boundary',
            content: resp.boundary_message || '',
            boundary_level: resp.boundary_level,
            original_text: text,
          });
          return;
        }

        // v3.83 - NLP-02: general knowledge override response
        if (resp.is_general_knowledge) {
          setClarifiers(null);
          appendMessage({
            id: makeId(),
            role: 'assistant',
            content: resp.message,
            is_general_knowledge: true,
            disclaimer: resp.disclaimer,
          });
          return;
        }

        if (resp.clarifiers && resp.clarifiers.length > 0 && resp.clarifier_context_id) {
          // v3.60 - Pipeline needs clarification before answering
          setClarifiers({
            question: resp.clarifiers[0].question,
            options: resp.clarifiers[0].options,
            allows_multiple: resp.clarifiers[0].allows_multiple,
            contextId: resp.clarifier_context_id,
            selected: [],
          });
        } else {
          setClarifiers(null);
          appendMessage({
            id: makeId(),
            role: 'assistant',
            content: resp.message,
            citations: resp.citations,
            latency_ms: resp.latency_ms,
            cache_hit: resp.cache_hit,
            // v3.77 - SHOW-01: trace entity ID for "How we found this" panel
            message_id: resp.message_id,
            // v3.94 - MISSED-ID-04 EDGS: employment decision risk
            risk_level: resp.risk_level,
            disclaimer_text: resp.disclaimer_text,
            // v4.04 - STORY-171: answer consistency change notification
            answer_changed: resp.answer_changed,
            divergence_pct: resp.divergence_pct,
            // v4.07 - AUDIT-12: inline confidence badge
            confidence_label: resp.confidence_label,
          });
        }
      } catch (err: any) {
        const status = err?.response?.status;
        let errorText = 'Something went wrong. Please try again.';
        if (status === 429) {
          const retryAfter = err?.response?.data?.retry_after;
          errorText = retryAfter
            ? `Rate limit reached. Please wait ${retryAfter} seconds before asking another question.`
            : 'Rate limit reached. Please wait a moment before trying again.';
        } else if (status === 503 || status === 502) {
          errorText = 'The analytics pipeline is temporarily unavailable. Please try again shortly.';
        } else {
          // v1.0 - CHAT-ERR-SAFE-LIST-01: show the backend reason only when it is
          // on the safe list; otherwise keep the generic message. - 2026-09-13
          errorText = resolveChatError(err, errorText);
        }
        appendMessage({ id: makeId(), role: 'error', content: errorText });
        setClarifiers(null);
      } finally {
        setIsLoading(false);
      }
    },
    [appendMessage]
  );

  // v3.60 - Public: user sends a text message
  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;

      appendMessage({ id: makeId(), role: 'user', content: trimmed });
      setClarifiers(null);
      await doSend(trimmed);
    },
    [isLoading, appendMessage, doSend]
  );

  // v3.83 - NLP-02: user clicked "Ask Anyway" on a boundary warning card
  const askAnyway = useCallback(
    async (originalText: string) => {
      const trimmed = originalText.trim();
      if (!trimmed || isLoading) return;
      // Replace the boundary card visually with a fresh user message
      appendMessage({ id: makeId(), role: 'user', content: trimmed });
      setClarifiers(null);
      await doSend(trimmed, undefined, undefined, true);
    },
    [isLoading, appendMessage, doSend]
  );

  // v3.60 - Toggle a clarifier option (supports single or multi-select)
  const selectClarifier = useCallback((option: string) => {
    setClarifiers(prev => {
      if (!prev) return prev;
      if (prev.allows_multiple) {
        const next = prev.selected.includes(option)
          ? prev.selected.filter(o => o !== option)
          : [...prev.selected, option];
        return { ...prev, selected: next };
      } else {
        return { ...prev, selected: [option] };
      }
    });
  }, []);

  // v3.60 - Submit selected clarifiers back to the pipeline
  const submitClarifiers = useCallback(async () => {
    if (!clarifiers || clarifiers.selected.length === 0 || isLoading) return;

    const label = clarifiers.selected.join(', ');
    appendMessage({ id: makeId(), role: 'user', content: label });

    const ctx = clarifiers.contextId;
    const selected = [...clarifiers.selected];
    setClarifiers(null);
    await doSend(label, ctx, selected);
  }, [clarifiers, isLoading, appendMessage, doSend]);

  // v3.60 - Reset chat and start a new session
  const reset = useCallback(() => {
    setMessages([]);
    setClarifiers(null);
    sessionIdRef.current = undefined;
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }, []);

  return { messages, isLoading, clarifiers, sendMessage, askAnyway, selectClarifier, submitClarifiers, reset };
}
